import { createContext, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getDeviceInfo } from './utils/deviceInfo';

const AuthContext = createContext();
export { AuthContext }; // <-- LIPSEA!

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);
  const lastRefreshRef = useRef(0);
  userRef.current = user;

  const syncOnboardingAnswers = async () => {
    try {
      const rawOnboardingAnswers = localStorage.getItem('onboardingAnswers');
      if (!rawOnboardingAnswers) return;

      const onboardingAnswers = JSON.parse(rawOnboardingAnswers);
      if (!onboardingAnswers || typeof onboardingAnswers !== 'object') return;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/user/onboarding-answers`,
        onboardingAnswers,
        {
          withCredentials: true,
          timeout: 5000,
        }
      );

      if (response.status >= 200 && response.status < 300) {
        localStorage.removeItem('onboardingAnswers');
      }
    } catch (err) {
      // Non-blocking: auth should continue even if onboarding sync fails.
    }
  };

  const registerDevice = async () => {
  try {
    const deviceInfo = getDeviceInfo();

    await axios.post(
      `${process.env.REACT_APP_API_URL}/device/register`,
      deviceInfo,
      {
        withCredentials: true,
        timeout: 5000,
      }
    );
  } catch (err) {
    // Non-blocking:
    // dacă device tracking eșuează,
    // autentificarea trebuie să funcționeze în continuare.
    console.log('Device registration failed:', err.message);
  }
};

// silent=true: refresh in fundal (focus/vizibilitate), fara a atinge `loading`
// (App.js redirectioneaza cand !loading && !user).
const checkAuth = async ({ silent = false } = {}) => {
  if (!silent) setLoading(true);
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/user`, {
      withCredentials: true,
      timeout: 5000
    });
    const userData = res.data;
    setUser(userData);

    await syncOnboardingAnswers();
    registerDevice();

    // 👇 Aplică tema global (fără context circular)
    if (typeof userData.dark_mode === 'boolean') {
      if (userData.dark_mode) {
        document.documentElement.classList.add('dark-mode');
      } else {
        document.documentElement.classList.remove('dark-mode');
      }
    }

    return userData;

  } catch (err) {
    console.log('Auth check failed:', err.message);
    const status = err.response && err.response.status;
    // Doar 401/403 inseamna "nu esti autentificat"; timeout/5xx/retea pastreaza userul curent.
    if (status === 401 || status === 403) {
      setUser(null);
      document.documentElement.classList.remove('dark-mode'); // fallback safe
      return null;
    }
    return userRef.current;
  } finally {
    if (!silent) setLoading(false);
  }
};


  useEffect(() => {
    lastRefreshRef.current = Date.now();
    checkAuth();
  }, []);

  // Reia userul cand tab-ul redevine activ (ex. cumparatura premium facuta in alt tab).
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'hidden' || !userRef.current) return;
      const now = Date.now();
      if (now - lastRefreshRef.current < 30000) return;
      lastRefreshRef.current = now;
      checkAuth({ silent: true });
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, checkAuth, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
